import { useEffect, useState, useMemo } from "react";
import {
  Autocomplete,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  CircularProgress,
  Typography
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useSelector } from "react-redux"; //for stack list
import SectionAccordion from "components/SectionAccordion";
//import ComboBox from "components/ComboBox";
//NOte: ComboBox has a weird rule for disableClear... so didn't use here (maybe could fix?)

const tableStyle = {
  tableLayout: "fixed",
  width: "100%",
  borderCollapse: "collapse",
};
//data in form { id: , name: }
function ReplaceTable({ edit1, allowCustom, initialData, possibleOptions, save, c1h="Current", c3h="Replace with:",
                        addToEnd = false, idxer = null, onlyIDs = true }) {
  const [data, setData] = useState(initialData.length > 0 ? initialData : addToEnd ? [{}] : []);
  const editId = (selectedIndex, newValue) => {
    const newData = [...data];
    newData[selectedIndex].id = newValue;
    const fData = newData.filter((v) => v.id || v.newid);
    if(addToEnd && (fData.length === 0 || fData[fData.length-1].id)) {
      fData.push({})
    }
    setData(fData);
    save(fData);
  }
  const editIdx = (selectedIndex, which, newValue) => {
    const allowed = /^[0-9]*$/;
    if (allowed.test(newValue)) {
      const newData = [...data];
      newData[selectedIndex][which] = newValue;
      setData(newData);
      save(newData);
    }
  }
  const handleChangeValue = (selectedIndex, newValue) => {
    const newData = [...data];
    if(newValue) {
      newData[selectedIndex].newid = newValue.id;
      newData[selectedIndex].newname = newValue.name || newValue.id;
    } else {
      delete newData[selectedIndex].newid;
      delete newData[selectedIndex].newname;      
    }
    if(addToEnd && newData[newData.length-1].id) {
      newData.push({})
    }
    setData(newData);
    save(newData);
  };
  return (
    <div>
      <Table style={tableStyle}>
        <TableHead>
          <TableRow>
            <TableCell>{c1h}</TableCell>
            <TableCell>{c3h}</TableCell>              
            {idxer && <TableCell>{idxer}</TableCell> }
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{edit1 ? (
                <TextField margin="dense" variant="standard" value={row.id || ""}
                           onChange={(event) => editId(index, event.target.value)}
                />
              ) : stackNameLoc(row) }
              </TableCell>
              <TableCell>
                <Autocomplete
                  freeSolo={allowCustom}
                  size="small"
                  options={ possibleOptions }
                  inputValue={ allowCustom ? (row.newid || "") : undefined }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onInputChange={(_, v, reason) => {
                    if (reason === "input") {
                      if(allowCustom) {
                        const filtered = onlyIDs ? v.replace(/[^A-Za-z0-9_.-]/g, "") : v;
                        handleChangeValue(index, {id: filtered});
                      }
                    }
                  }}
                  onChange={ (_, v) => handleChangeValue(index, v) }
                  getOptionLabel={(option) => (option.newname || option?.newid || option?.name || option?.id || "") }
                  getOptionKey={(option) => option?.id}
                  renderInput={(params) => {
                    const endAdornment = (allowCustom && possibleOptions?.length) ? { endAdornment:
                      <>
                        <IconButton
                          size="small"
                          sx={{ pointerEvents: "none" }} 
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                        {params.InputProps.endAdornment}
                      </>} : {};
                    ;
                    return (
                    <TextField {...params} label=""
                               variant="standard"
                               margin="dense"
                               inputProps={{
                                 ...params.inputProps,
                               }}
                               InputProps={{
                                 ...params.InputProps,
                                 disableUnderline: false,
                                 style: { paddingBottom: 0 },
                                 ...endAdornment
                               }}
                               InputLabelProps={{
                                 style: { fontSize: "0.75rem" },
                               }}
                    />)}}                  
                />
              </TableCell>
              { idxer &&
                <TableCell>
                  <TextField margin="dense" variant="standard" value={row.idx1}
                             label="Start of range"
                             inputMode="numeric"
                             inputProps={{ maxLength:2 }}
                             width = "4"
                             onChange={(event) => {editIdx(index, "idx1", event.target.value)}}/>
                  <TextField margin="dense" variant="standard" value={row.idx2}
                             label="End of range"
                             inputMode="numeric"
                             inputProps={{ maxLength:2, pattern: "[0-9]*" }}                             
                             onChange={(event) => { editIdx(index, "idx2", event.target.value)}}/>
                  <TextField margin="dense" variant="standard" value={row.newidx}
                             inputProps={{ maxLength:2, pattern: "[0-9]*" }}
                             inputMode="numeric"
                             label="New start of range"
                             onChange={(event) => { editIdx(index, "newidx", event.target.value)}}/>
                </TableCell>
              }
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

//utility
function stackName(s) {
    try {
        return (s.name || JSON.parse(s.dev_session)?.["stack.label"] || s.guuid);
    } catch {
        return s.guuid || "Unknown";
    }
}
function stackNameLoc(s) {
  return <span> {s.name || s.id || ""} { s.caption && <span style={{color:"gray"}}> {s.caption} </span> } </span>
}

function changeVals(a, filterNull=false) {
  return a.reduce((acc, v) => { if(v.id && (!filterNull || v.newid?.trim())) acc[v.id] = v.newid?.trim(); return acc; }, {});
}

function DashboardPropertiesDialog({dashboard, save, open, handleClose, saveDuplicate, widgets, locked, owner, share}) {
  const [requestPending, setRequestPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [changes, setChanges] = useState({});
  const [expertChanges, setExpertChanges] = useState([]);
  const [searchReplaces, setSearchReplaces] = useState([{id:""}]);
  const userStacks = useSelector((state) => state.isx.stacks);
  const orgs = useSelector((state) => state.isx.orgs);
  const groups = Object.values(orgs || []).map((g) => { return {id: g.guuid, name: g.name}});

  //stacks available to user
  const stackOptions = Object.values(userStacks).map((s) => { return {name: stackName(s), id: s.guuid }});
  const [dashboardGroups, setDashboardGroups] = useState([]);

  const updateDashboardGroups = useMemo(() => {
    return (gr) => {
      setDashboardGroups(gr.map((g) => {return { id: g, name: orgs[g]?.name}} ));
    }}, [setDashboardGroups, orgs]);
  
  const dashboardGroupsChanged = useMemo(() => {
    const setEqual = (xs, ys) =>
          xs.size === ys.size &&
          [...xs].every((x) => ys.has(x));
    const groups = dashboardGroups.map((g)=> g.id);
    return (!setEqual(new Set(dashboard.acu_groups || []), new Set(groups)));
  },[dashboardGroups, dashboard.acu_groups]);
  
  useEffect(() => {
    updateDashboardGroups(dashboard.acu_groups ?? []);
  }, [dashboard.acu_groups, updateDashboardGroups]);

  function updateChanges(a, v) {
    if(Object.keys(v).length === 0) {
      if(changes[a]) {
        const ch = {...changes};
        delete ch[a];
        setChanges(ch);
      }
    } else {
      setChanges({...changes, [a] : v});
    }
  }
    
  const myHandleClose = () => {
    setChanges({});
    setExpertChanges([]);
    setSearchReplaces([{id:""}]);
    updateDashboardGroups(dashboard.acu_groups ?? []);
    handleClose();
  }
  const stacksHere = useMemo(
      () => [...Object.values(widgets).reduce(
          (acc, curr) => { curr.tags?.map((t) => {if(t.stack) acc.add(t.stack); return acc});
                           return acc; }, new Set())]
      .filter((g)=>(g in userStacks))
      .map((g) => { return {id: g, caption: "("+userStacks[g]?.location+")", name:stackName(userStacks[g])}}),
    [widgets, userStacks]
  );
  const tagsHere = useMemo(
    () => Object.entries(Object.values(widgets).reduce(
      (acc, curr) => { curr.tags?.map((t) => {
        if(t.attribute) { acc[t.attribute] = (acc[t.attribute] || 0) + 1 };
        return null;
      }); return acc; }, {} ))
      .map(([k,v]) => { return {id: k, name: k, caption: v > 1 ? ("("+v+" occurances)") : ""}}),
    [widgets]);
  const tagsHereOptions = useMemo(
    () => {
      return Object.values([...new Set([...stacksHere.map((s)=>s.id),
                                        ...new Set(Object.values(changes?.["__replace.widgets.tags.stack"] || {}))])]
                           .reduce((acc, s) => { Object.keys(userStacks[s]?.data_config || {}).map((a) => {
                             if(a !== 'dsitems' && !acc[a]) acc[a] = ({id: a, name:a, caption: ''});
                             return null;
                           }); return acc}, {}));
    }, [stacksHere, changes, userStacks]
  );
  const titlesHere = useMemo(
    () => Object.entries(Object.values(widgets).reduce( 
      (acc, curr) => { if(curr.title) { acc[curr.title] = (acc[curr.title] || 0) + 1 }; return acc}, {}))
      .map(([k,v]) => { return {id: k, name: k, caption: v > 1 ? ("("+v+" occurances)") : ""}}),
    [widgets]
  );
  const showExpertTagReplacer = useMemo(() => Object.values(tagsHere.reduce( (acc, t) => {const match = t.id.match(/^([A-Za-z_]+)\d{2}\./); if(match) acc[match[1]] = {id: match[1]}; return acc; }, {})), [tagsHere]);
  //[{id: "abc"}, {id: "def"}];
  
    /* 
     *   const stackattrsInDashboard = useMemo(
     *       () => [... Object.values(widgets).reduce(
     *           (acc, curr) => { curr.tags?.map((t) => {if(t.stack) acc.add(t.stack+"-"+t.attribute); return acc});
     *               return acc; }, new Set())],
     *       [widgets]
     *   );
     *   const attrsInDashboard = useMemo(
     *       () => [... Object.values(widgets).reduce(
     *           (acc, curr) => { curr.tags?.map((t) => {if(t.attribute) acc.add(t.attribute); return acc});
     *               return acc; }, new Set())],
     *       [widgets]
     *   );
     *  */
  function processExpertChanges(ch) {
    const regexs = ch && ch.reduce((acc, row) => {
      if(row.newid || row.newidx != null) {
        if(row.idx1 && row.newidx) {
          const lasti = row.idx2 != null ? parseInt(row.idx2) : 99;
          const delta = parseInt(row.newidx)-parseInt(row.idx1);
          for(let num=parseInt(row.idx1); num <= lasti; num++) {
            acc["^[A-Za-z_]+"+num.toString().padStart(2, '0')+"\\."] = (row.newid || row.id) + (num + delta).toString().padStart(2, '0') + "."
          }
        } else if(row.newid) {
          acc["^[A-Za-z_]+(\\d{2}\\.)"] = row.newid + "\\1";
        }
      }
      return acc;},{});
    return regexs && Object.keys(regexs).length ? {'__replacere.widgets.tags.attribute': regexs} : {};
  }
  async function saveDashboard() {
    if(dashboardGroupsChanged) { //change group sharing
      const groups = dashboardGroups.map((g)=> g.id);
      console.log("Changing Dashboard sharing", dashboard.guuid, groups);
      const results = await share(groups);
      if (results.error) {
        setErrorMessage(results.error);
        setRequestPending(false);
        return;
      }
    }
    const pExpertChanges = processExpertChanges(expertChanges);
    if((changes && Object.keys(changes).length > 0)||(pExpertChanges && Object.keys(pExpertChanges).length > 0)) {
        setRequestPending(true);
        //args: implemented in backend as __replacestr.<path> : { replace1: replacement1, replace2: replacment2 ....}
        //this code could me much cleaner...(here and below in TextField UI handlers)
        const results = await save({...changes, ...pExpertChanges});
        if (results.error) {
            setErrorMessage(results.error);
        } else {
          myHandleClose();
        }
    } else {
      myHandleClose();
    }
    setRequestPending(false);
  }
  async function saveDashboardDuplicate() {
    setRequestPending(true);
    try {
      //note: have to empty sharing groups here - until we adjust backend to do the correct thing on dashboard create with 'acu_groups' attr
      await saveDuplicate({...changes, ...processExpertChanges(expertChanges), acu_groups: []}, dashboardGroups.map((g)=>g.id));
      //todo: I believe that the above is going to navigate - so the rest of this may not matter....
      setRequestPending(false);
      myHandleClose();
      setChanges({});
      setExpertChanges([]);
      updateDashboardGroups(dashboard.acu_groups ?? []);
    } catch (err) {
      setErrorMessage(err);
    }
  }

  return (
    <Dialog open={open} fullWidth maxWidth='full' aria-labelledby="template-dialog-title">
      <DialogTitle id="template-dialog-title">
        Manage/Copy Dashboard
      </DialogTitle>
      <DialogContent>
        <Typography>
          Use this tool to quickly swap data sources and/or tags for the entire dashboard.
          Use 'Save as Duplicate' button to create a duplicate dashboard with these settings.
        </Typography>
        <SectionAccordion title="Dashboard Properties" defaultExpanded="true">
          <FormControl margin="dense" fullWidth>
            <TextField
              fullwidth
              label="Name"
              variant="standard"
              value={changes.title || dashboard.title || ""}
              onChange={(event) => {
                setChanges({...changes, title: event.target.value})
              }}
              margin="dense"
            />
          </FormControl>
          <Autocomplete
            multiple
            options={groups}
            getOptionLabel={(option) => option.name}
            value={dashboardGroups}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(e, val) => setDashboardGroups(
              val.filter((v, i, arr) => arr.findIndex(x => x.id === v.id) === i))}
            renderInput={(params) => (
              <TextField {...params} label="Share With Groups" placeholder="" />
            )}
            sx={{ width: '500px' }}
          />          
        </SectionAccordion>
        <SectionAccordion title="Stacks in Use">
          <ReplaceTable c1h="Stack Name"
                        save={(a) => {updateChanges('__replace.widgets.tags.stack', changeVals(a,true))}}
                        initialData={stacksHere} possibleOptions={stackOptions}/>          
        </SectionAccordion>
        <SectionAccordion title="Tag IDs in Use">
          <ReplaceTable c1h="Tag ID" c3h="Replace with ID (Case Sensitive)"
                        save={(a) => {updateChanges('__replace.widgets.tags.attribute', changeVals(a,true))}}
                        initialData={tagsHere} possibleOptions={tagsHereOptions} allowCustom={true}/>
        </SectionAccordion>
        { Object.keys(showExpertTagReplacer).length > 0 && 
          <SectionAccordion title="Indexed Tag Editor">
            <ReplaceTable c1h="Indexed Tag ID Prefix" idxer="Index Range to be replaced (inclusive)"
                          c3h="Replace Prefix"
                          save={(a) => {setExpertChanges(a)}}
                          initialData={showExpertTagReplacer} possibleOptions={[]} allowCustom={true}/>
          </SectionAccordion>
        }
        <SectionAccordion title="Widget Names">
          <ReplaceTable edit1={true} addToEnd={true} c1h="Find String in All Widget Titles"
                        save={(a) => {updateChanges('__replacestr.widgets.title', changeVals(a))}}
                        onlyIDs={false}                        
                        initialData={searchReplaces} possibleOptions={[]} allowCustom={true}/>
          <br/>
          <br/>
          <ReplaceTable c1h="Widget name" save={(a) => {updateChanges('__replace.widgets.title', changeVals(a))}}
                        onlyIDs={false}
                        initialData={titlesHere} possibleOptions={[]} allowCustom={true}/>
        </SectionAccordion>
        {errorMessage && (
          <DialogContentText style={{ color: "red" }}>
            {errorMessage}
          </DialogContentText>
        )}
        { locked && <DialogContentText align="right">
                      <br/> <br/> 
                      Unlock to manage this Dashboard
                    </DialogContentText>}
      </DialogContent>
      <DialogActions>
        <Button onClick={myHandleClose} style={{ color: "red" }}>
          Cancel
        </Button>
        <Button
          disabled={requestPending || locked || (Object.keys(changes).length === 0 && !dashboardGroupsChanged && Object.keys(expertChanges).length === 0)}
          onClick={saveDashboard}
          color="primary"
        >
          Apply Changes
        </Button>
        <Button
          disabled={requestPending}
          onClick={saveDashboardDuplicate}
        >
          Save as Duplicate
        </Button>
        {requestPending && <CircularProgress size={20} color="primary" />}
      </DialogActions>
    </Dialog>
  );
};

export default DashboardPropertiesDialog;




